/* eslint-disable max-lines */
import { authenticate } from '@cian/auth';
import { Button, ButtonTheme } from '@cian/button-component';
import { CheckboxField, mergeStyles, MultilineTextField, TextField, Tooltip, TooltipPosition } from '@cian/components';
import * as React from 'react';

import { IPostReviewFormResponse200 } from '../../api/review_request';
import { IReviewRequestData } from '../../store/review_request';
import { isEmail, isFakeEmail, isPhoneNumber } from '../../utils/validate';
import { RecaptchaContainer } from '../recaptcha';
import * as styles from './index.css';

enum STAGES {
  TEXT = 1,
  FORM = 2,
  CAPTCHA = 3,
  SUCCESS = 4,
}

interface IReviewFormState {
  stage: STAGES;
  text: string;
  isLoading: boolean;
  captcha: string | null;
  email: string;
  emailErrorText: string;
  name: string;
  phone: string;
  subscribeForNews: boolean;
  isResponseError: boolean;
  textErrorText: string;
  phoneErrorText: string;
  nameErrorText: string;
}

export interface IReviewFormStoreProps {
  isFromBuilder: boolean;
  isMessagingEnabled: boolean;
  email?: string | null;
  phone?: string | null;
  name?: string | null;
  isCommercial?: boolean;
  isPik?: boolean;
  reviewRequestData: IReviewRequestData;
}

export interface ISendFormDataOptions {
  message: string;
  name: string;
  email: string;
  phoneNumber: string;
  captcha: string;
  subscribeForNews: boolean;
}

export interface IReviewFormDispatchProps {
  reviewRequest(options: ISendFormDataOptions): Promise<IPostReviewFormResponse200>;
  resetReviewRequest(): void;
  updateUserData(): void;
}

type IReviewFormProps = IReviewFormStoreProps & IReviewFormDispatchProps;

export class ReviewForm extends React.Component<IReviewFormProps, IReviewFormState> {
  public constructor(props: IReviewFormProps) {
    super(props);

    this.state = {
      captcha: null,
      email: props.email && !isFakeEmail(props.email) ? props.email : '',
      emailErrorText: '',
      isLoading: false,
      isResponseError: false,
      name: props.name || '',
      nameErrorText: '',
      phone: props.phone || '',
      phoneErrorText: '',
      stage: STAGES.TEXT,
      subscribeForNews: false,
      text: '',
      textErrorText: '',
    };
  }

  public render() {
    const { isMessagingEnabled, isCommercial, isPik, isFromBuilder } = this.props;

    const isVisible = (isMessagingEnabled && isCommercial) || (isPik && isFromBuilder);

    if (isVisible) {
      return (
        <div className={styles['container']}>
          {this.state.stage === STAGES.TEXT && this.renderTextStage()}
          {this.state.stage === STAGES.FORM && this.renderFormStage()}
          {this.state.stage === STAGES.CAPTCHA && this.renderCaptchaState()}
          {this.state.stage === STAGES.SUCCESS && this.renderSuccessStage()}
        </div>
      );
    }

    return null;
  }

  private renderFormTitle() {
    return this.props.isCommercial ? 'Заявка' : 'Заявка на просмотр объекта';
  }

  private renderFormStage() {
    return (
      <div className={styles['form-container']}>
        <h3 className={styles['title']}>{this.renderFormTitle()}</h3>
        <div className={styles['form']}>
          <Tooltip tooltipContent={this.state.nameErrorText} position={TooltipPosition.BOTTOM_WIDE}>
            <TextField
              invalid={Boolean(this.state.nameErrorText)}
              disabled={this.state.isLoading}
              placeholder="Ваше имя"
              value={this.state.name}
              onValueChange={this.handleNameChange}
            />
          </Tooltip>
          <div className={styles['form-row']}>
            <Tooltip
              multiline
              tooltipContent={this.state.phoneErrorText}
              tooltipStyles={styles['row-field']}
              position={TooltipPosition.BOTTOM_WIDE}
            >
              <TextField
                mask="+7 (999) 999-99-99"
                disabled={this.state.isLoading}
                invalid={Boolean(this.state.phoneErrorText)}
                placeholder="Номер телефона"
                value={this.state.phone}
                onValueChange={this.handlePhoneChange}
              />
            </Tooltip>
            <Tooltip
              multiline
              tooltipStyles={styles['row-field']}
              tooltipContent={this.state.emailErrorText}
              position={TooltipPosition.BOTTOM_WIDE}
            >
              <TextField
                placeholder="Эл. почта"
                disabled={this.state.isLoading}
                value={this.state.email}
                onValueChange={this.handleEmailChange}
              />
            </Tooltip>
          </div>
          <div className={styles['checkbox-container']}>
            <CheckboxField
              containerStyle={[styles['checkbox-field']]}
              disabled={this.state.isLoading}
              labelStyle={styles['checkbox']}
              label={<div>Хочу получать новости Cian.ru</div>}
              value={this.state.subscribeForNews}
              onValueChange={this.handleSubscribeForNewsChange}
            />
          </div>
          <p className={styles['legal']}>
            При отправке вы принимаете условия{' '}
            <a href="/help/about/rules-legal/" target="_blank">
              Пользовательского соглашения
            </a>{' '}
            и{' '}
            <a href="/help/about/36526/" target="_blank">
              Политики конфиденциальности
            </a>
          </p>
        </div>
        <div className={styles['footer']}>
          <Button
            {...mergeStyles([
              styles['send-button'],
              this.state.isLoading && styles['send-button--loading'],
            ] as string[])}
            onClick={this.handleSendRequestClick}
          >
            <span className={styles['send-text']}>Отправить заявку</span>
          </Button>
          <button
            {...mergeStyles([styles['button-back'], styles['button-text']])}
            onClick={this.handlePrevStageFormClick}
          >
            Назад
          </button>
          {this.renderStage()}
        </div>
        {this.state.isResponseError && (
          <div className={styles['error']}>
            <div className={styles['error-title']}>Не удалось отправить заявку</div>
            <div className={styles['error-text']}>Попробуйте ещё раз</div>
          </div>
        )}
      </div>
    );
  }

  private renderSuccessStage() {
    return (
      <div className={styles['success']}>
        <div className={styles['success-title']}>Заявка отправлена</div>
        <button className={styles['button-text']} onClick={this.handleSuccessClick}>
          Отправить ещё
        </button>
      </div>
    );
  }

  private handlePrevStageFormClick = () => {
    this.setState({
      stage: STAGES.TEXT,
    });
  };

  private renderTextStage() {
    return (
      <div>
        <h3 className={styles['title']}>{this.renderFormTitle()}</h3>
        <Tooltip tooltipContent={this.state.textErrorText} position={TooltipPosition.BOTTOM_WIDE}>
          <MultilineTextField
            inputStyle={[styles['textarea']]}
            minRows={3}
            invalid={Boolean(this.state.textErrorText)}
            placeholder={
              this.props.isCommercial
                ? 'Заинтересовало ваше предложение, просьба прислать дополнительную информацию'
                : 'Хочу посмотреть двухкомнатные квартиры с видом во двор'
            }
            value={this.state.text}
            onValueChange={this.handleTextAreaChange}
          />
        </Tooltip>
        <div className={styles['footer']}>
          <Button theme={ButtonTheme.Ghost} onClick={this.handleNextClick}>
            Продолжить
          </Button>
          {this.renderStage()}
        </div>
      </div>
    );
  }

  private renderCaptchaState() {
    return (
      <div>
        <h3 className={styles['title']}>Подтвердите, что вы не робот и заявка будет отправлена</h3>
        <div className={styles['recaptcha']}>
          <RecaptchaContainer onValueChange={this.handleRecaptchaChange} />
        </div>
        <div className={styles['footer']}>
          <button
            {...mergeStyles([styles['button-text'], styles['recaptcha-back']])}
            onClick={this.handlePrevStageCaptchaClick}
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  private handleRecaptchaChange = (value: string | null) => {
    if (value) {
      this.setState({
        captcha: value,
        isLoading: true,
        stage: STAGES.FORM,
      });
      this.sendFormData(value);
    } else {
      this.setState({
        captcha: value,
      });
    }
  };

  private handlePrevStageCaptchaClick = () => {
    this.setState({
      stage: STAGES.FORM,
    });
  };

  private handleSendRequestClick = () => {
    if (this.state.isLoading) {
      return;
    }

    let nameErrorText = '';
    let emailErrorText = '';
    let phoneErrorText = '';

    if (!this.state.name.trim()) {
      nameErrorText = 'Введите имя';
    }

    if ((this.state.subscribeForNews || this.state.email) && !isEmail(this.state.email)) {
      emailErrorText = 'Неверный e-mail адрес';
    }

    if (!this.state.phone.trim()) {
      phoneErrorText = 'Введите телефон';
    } else if (!isPhoneNumber(this.state.phone)) {
      phoneErrorText = 'Неверный номер телефона';
    }

    if (phoneErrorText || emailErrorText || nameErrorText) {
      this.setState({
        emailErrorText,
        nameErrorText,
        phoneErrorText,
      });

      return;
    }

    if (this.state.captcha) {
      this.setState({
        isLoading: true,
      });

      this.sendFormData(this.state.captcha);
    } else {
      this.setState({
        stage: STAGES.CAPTCHA,
      });
    }
  };

  private sendFormData(captcha: string) {
    const text = this.state.text.trim();
    const name = this.state.name.trim();
    const email = this.state.email.trim();
    const phone = this.state.phone.replace(/[^+\d]/g, '');
    const message = this.props.isCommercial ? `${text} ${email} ${name} ${phone}` : text;

    this.props
      .reviewRequest({
        captcha,
        email,
        message,
        name,
        phoneNumber: phone,
        subscribeForNews: this.state.subscribeForNews,
      })
      .then(response => {
        this.setState({
          captcha: null,
          isLoading: false,
          stage: STAGES.SUCCESS,
        });

        if (response.data.isUserRegistered && response.data.logOnData) {
          authenticate(response.data.logOnData.logOnInfo, response.data.logOnData.login, true)
            .then(() => {
              if (window.__reloadHeader__) {
                window.__reloadHeader__();
              }

              this.props.updateUserData();
            })
            .catch(e => {
              throw e;
            });
        }
      })
      .catch(() => {
        this.setState({
          captcha: null,
          isLoading: false,
          isResponseError: true,
        });
      });
  }

  private handleNextClick = () => {
    const emailTrimmed = this.state.text.trim();

    if (!emailTrimmed) {
      this.setState({ textErrorText: 'Введите текст заявки' });
    } else {
      this.setState({ stage: STAGES.FORM });
    }
  };

  private handleSuccessClick = () => {
    this.props.resetReviewRequest();

    this.setState({
      isResponseError: false,
      stage: STAGES.TEXT,
      text: '',
    });
  };

  private handleTextAreaChange = (text: string) => {
    this.setState({
      text,
      textErrorText: '',
    });
  };

  private handleEmailChange = (email: string) => {
    this.setState({
      email,
      emailErrorText: '',
      nameErrorText: '',
      phoneErrorText: '',
      textErrorText: '',
    });
  };

  private handlePhoneChange = (phone: string) => {
    this.setState({
      emailErrorText: '',
      nameErrorText: '',
      phone,
      phoneErrorText: '',
      textErrorText: '',
    });
  };

  private handleNameChange = (name: string) => {
    this.setState({
      emailErrorText: '',
      name,
      nameErrorText: '',
      phoneErrorText: '',
      textErrorText: '',
    });
  };

  private handleSubscribeForNewsChange = (subscribeForNews: boolean) => {
    this.setState({
      emailErrorText: '',
      nameErrorText: '',
      phoneErrorText: '',
      subscribeForNews,
      textErrorText: '',
    });
  };

  private renderStage() {
    return <div className={styles['steps']}>{this.state.stage === STAGES.TEXT ? 'Шаг 1 из 2' : 'Шаг 2 из 2'}</div>;
  }
}
